import React, { useState } from 'react';
import { View, Image, ActivityIndicator, Text, StyleSheet } from 'react-native';
import type { ImageProps, ImageStyle, StyleProp } from 'react-native';
import Colors from 'src/core/utils/Colors';
import Fonts from 'src/core/utils/Fonts';
import Dimens from 'src/core/utils/Dimens';
import Translations from 'src/core/utils/Translations';

interface RemoteImageProps {
	uri: string;
	style: StyleProp<ImageStyle>;
	resizeMode?: ImageProps['resizeMode'];
}

/**
 * Shows an image from a remote URL with a spinner while loading and a grey placeholder with a message when
 * the image cannot be loaded.
 */
export default function RemoteImage({ uri, style, resizeMode = 'cover' }: RemoteImageProps) {
	const [loading, setLoading] = useState(true);
	const [failed, setFailed] = useState(false);

	return (
		<View style={[style, styles.base]}>
			{!failed && (
				<Image
					resizeMode={resizeMode}
					source={{ uri }}
					style={StyleSheet.absoluteFill}
					testID="remote-image"
					onError={() => {
						setLoading(false);
						setFailed(true);
					}}
					onLoadEnd={() => setLoading(false)}
				/>
			)}

			{loading && !failed ? <ActivityIndicator color={Colors.darkGrey} size="small" testID="remote-image-loader" /> : null}

			{failed ? <Text style={styles.errorText}>{Translations.errorImageLoad}</Text> : null}
		</View>
	);
}

const styles = StyleSheet.create({
	base: {
		backgroundColor: Colors.greyBg,
		justifyContent: 'center',
		alignItems: 'center',
		overflow: 'hidden',
	},
	errorText: {
		paddingHorizontal: Dimens.padding16,
		textAlign: 'center',
		color: Colors.greyTextDark,
		fontFamily: Fonts.montserratRegular,
		fontSize: Dimens.fontSize14,
	},
});
